import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Outlet, useMatch, useParams, useRoutes } from 'react-router';
import { NavLink } from 'react-router-dom';
import { ChevronRight } from 'lucide';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// common
import Icon from 'common/components/Icon';
import { some } from 'common/constants';
import useHookQuery from 'common/hook/useHookQuery';
import { IRouteObject } from 'common/types';
import { setOpenAsideBar } from '@redux/cacheReducer';
import { PROJECT_LIST_ROUTES } from 'layout/routes';
import { getBrand } from 'config/brand';
import { SHELL } from 'config/setupTheme';

interface Props {
  hidden?: boolean;
}

const ActiveTitleContext = React.createContext<(titleId?: string) => void>(() => {});

function BreadcrumbsItem(props) {
  const { title, disabled, portalId = '', hidden, collapseWhenIn, path, depth } = props;
  const params = useParams<some>();
  const queryScreen = useHookQuery();
  const { isMobile } = queryScreen;
  const intl = useIntl();
  const dispatch = useDispatch();
  const setActiveTitle = React.useContext(ActiveTitleContext);
  const matchRoutes = useMatch(path) as any;
  const isActive = !!matchRoutes;

  function fillValues(str, obj) {
    return str.replace(/:\w+/g, (match) => {
      return obj[match.slice(1)] || match;
    });
  }

  React.useEffect(() => {
    if (isActive) {
      document.title =
        getBrand().app_name + (title ? ' | ' + intl.formatMessage({ id: title }) : '');
    }
  }, [intl, isActive, title]);

  React.useLayoutEffect(() => {
    if (isActive && title) setActiveTitle(title);
  }, [isActive, setActiveTitle, title]);

  React.useEffect(() => {
    if (isActive && collapseWhenIn && collapseWhenIn(queryScreen)) {
      dispatch(setOpenAsideBar(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseWhenIn, dispatch, isActive]);

  if (hidden) {
    return <Outlet />;
  }

  return (
    <>
      {isActive ? (
        <>
          {portalId ? (
            <Typography variant="caption" component={'div'} id={portalId} />
          ) : (
            <Typography variant="caption">{title && <FormattedMessage id={title} />}</Typography>
          )}
        </>
      ) : (
        <>
          {disabled ? (
            <>
              {portalId ? (
                <Typography
                  variant="caption"
                  component={'div'}
                  color="textSecondary"
                  id={portalId}
                />
              ) : (
                <Typography variant="caption" color="textSecondary">
                  {title && <FormattedMessage id={title} />}
                </Typography>
              )}
            </>
          ) : (
            <NavLink to={fillValues(path, params) || '/'}>
              {isMobile && depth ? (
                <Typography variant="caption">...</Typography>
              ) : (
                <Typography variant="caption">
                  {portalId ? (
                    <Typography variant="caption" component={'div'} id={portalId} />
                  ) : (
                    <Typography variant="caption">
                      {title && <FormattedMessage id={title} />}
                    </Typography>
                  )}
                </Typography>
              )}
            </NavLink>
          )}
          &nbsp;
          <Icon icon={ChevronRight} size={16} />
          &nbsp;
        </>
      )}
      <Outlet />
    </>
  );
}

const DefaultBreadcrumb: React.FunctionComponent<Props> = (props) => {
  const { hidden } = props;
  const queryScreen = useHookQuery();
  const { isMobile } = queryScreen;
  const [activeTitleId, setActiveTitleId] = React.useState<string>();
  const setActiveTitle = React.useCallback((titleId?: string) => {
    setActiveTitleId((current) => (current === titleId ? current : titleId));
  }, []);
  const mappedRoutes = (list: IRouteObject[], depth: number, parent?: IRouteObject) => {
    return list.map((one) => {
      let absolutePath = one.index
        ? parent?.absolutePath?.replace('/*', '')
        : (parent?.absolutePath?.replace('/*', '') || '') + '/' + one?.path?.replace('/*', '');

      if (one.blockBreadcrumb) {
        absolutePath = absolutePath + '/*';
      }

      return {
        ...one,
        path: one.index ? parent?.path : one?.path,
        element: (
          <BreadcrumbsItem
            title={one.label || one.title}
            portalId={one.portalId}
            disabled={
              (one?.children && one?.children?.length === 0) || !one.children?.find((v) => v.index)
            }
            hidden={hidden}
            collapseWhenIn={one.collapseWhenIn}
            path={absolutePath}
            breadcrumb={one.breadcrumb}
            depth={depth}
          />
        ),
        children:
          one.children &&
          !one.blockBreadcrumb &&
          mappedRoutes(one.children, depth + 1, {
            ...one,
            absolutePath,
          }),
      };
    });
  };

  const breadcrumbsElements = useRoutes(mappedRoutes(PROJECT_LIST_ROUTES, 0));

  if (hidden) {
    return breadcrumbsElements;
  }

  if (!activeTitleId) {
    return (
      <ActiveTitleContext.Provider value={setActiveTitle}>
        <Box sx={{ display: 'none' }}>{breadcrumbsElements}</Box>
      </ActiveTitleContext.Provider>
    );
  }

  return (
    <ActiveTitleContext.Provider value={setActiveTitle}>
      <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          sx={{
            width: 3,
            alignSelf: 'stretch',
            borderRadius: 99,
            bgcolor: SHELL.accent,
            flexShrink: 0,
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              lineHeight: 1.45,
              fontSize: isMobile ? 18 : 22,
              letterSpacing: '-0.02em',
              color: 'text.primary',
              overflow: 'visible',
            }}
          >
            {activeTitleId ? <FormattedMessage id={activeTitleId} /> : null}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            color="text.secondary"
            flexWrap="wrap"
            sx={{
              mt: 0.25,
              minWidth: 0,
              '& .MuiTypography-caption': { fontSize: 12.5, lineHeight: 1.4 },
            }}
          >
            {breadcrumbsElements}
          </Box>
        </Box>
      </Box>
    </ActiveTitleContext.Provider>
  );
};

export default React.memo(DefaultBreadcrumb);
